import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FileText,
  Users,
  Package,
  LogOut,
  BarChart3,
  Building2,
  Ship,
  Layers,
  Download,
  TrendingUp,
  DollarSign,
  AlertCircle,
  X
} from 'lucide-react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { authService } from '../services/authService';
import informeService from '../services/informeService';

function Informes() {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [equiposPorTipo, setEquiposPorTipo] = useState([]);
  const [equiposPorCliente, setEquiposPorCliente] = useState([]);
  const [facturacion, setFacturacion] = useState(null);
  const [historial, setHistorial] = useState([]);

  // Mes anterior por defecto (para evitar consultar mes en curso al cargar)
  const getMesAnterior = () => {
    const hoy = new Date();
    const mesAnteriorIdx = hoy.getMonth(); // 0-11 (mes actual - 1)
    const anio = mesAnteriorIdx === 0 ? hoy.getFullYear() - 1 : hoy.getFullYear();
    const mes = mesAnteriorIdx === 0 ? 12 : mesAnteriorIdx;
    return { mes, anio };
  };
  const mesAnteriorData = getMesAnterior();

  const [mesSeleccionado, setMesSeleccionado] = useState(mesAnteriorData.mes);
  const [anioSeleccionado, setAnioSeleccionado] = useState(mesAnteriorData.anio);

  // Filtro de fechas del Historial de Cambios
  const [historialPreset, setHistorialPreset] = useState('30dias');
  const [historialInicio, setHistorialInicio] = useState('');
  const [historialFin, setHistorialFin] = useState('');

  const toYMD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Devuelve los params del historial según el preset seleccionado
  const getRangoHistorial = () => {
    const hoy = new Date();
    if (historialPreset === 'este_mes') {
      const inicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
      return { fecha_inicio: toYMD(inicio), fecha_fin: toYMD(hoy) };
    }
    if (historialPreset === 'mes_anterior') {
      const inicio = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
      const fin = new Date(hoy.getFullYear(), hoy.getMonth(), 0); // último día del mes anterior
      return { fecha_inicio: toYMD(inicio), fecha_fin: toYMD(fin) };
    }
    if (historialPreset === 'personalizado') {
      const params = {};
      if (historialInicio) params.fecha_inicio = historialInicio;
      if (historialFin) params.fecha_fin = historialFin;
      return Object.keys(params).length ? params : { dias: 30 };
    }
    return { dias: 30 };
  };

  const [showDetalle, setShowDetalle] = useState(false);
  const [detalleData, setDetalleData] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [loadingHistorial, setLoadingHistorial] = useState(false);

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

  const meses = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' }
  ];

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mesSeleccionado, anioSeleccionado]);

  // El historial se carga de forma independiente para no recargar toda la vista
  useEffect(() => {
    loadHistorial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historialPreset, historialInicio, historialFin]);

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const promises = [
        informeService.getEquiposPorTipo(),
        informeService.getEquiposPorCliente(),
      ];

      if (user?.rol === 'admin' || user?.rol === 'cliente') {
        promises.push(informeService.getFacturacionMensual(mesSeleccionado, anioSeleccionado));
      }

      const results = await Promise.all(promises);
      const [tiposData, clientesData, facturacionData] = results;

      setEquiposPorTipo(tiposData);
      setEquiposPorCliente(clientesData);
      setFacturacion(facturacionData || null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // Carga (o recarga) solo el historial de cambios según el filtro de fechas
  const loadHistorial = async () => {
    try {
      setLoadingHistorial(true);
      const data = await informeService.getHistorialCambios(getRangoHistorial());
      setHistorial(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoadingHistorial(false);
    }
  };

  const loadDetalleFacturacion = async (clienteId) => {
    try {
      setLoadingDetalle(true);
      setError('');
      const data = await informeService.getFacturacionDetalle(clienteId, mesSeleccionado, anioSeleccionado);
      setDetalleData(data);
      setShowDetalle(true);
    } catch (err) {
      setError(err);
    } finally {
      setLoadingDetalle(false);
    }
  };

  const handleCerrarDetalle = () => {
    setShowDetalle(false);
    setDetalleData(null);
  };

  const exportarCSV = (data, filename) => {
    const csv = data.map(row => Object.values(row).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
  };

  const exportarPDF = async () => {
    if (!facturacion || facturacion.clientes.length === 0) {
      setError('No hay datos de facturación para exportar');
      return;
    }

    const hoy = new Date();
    const mesActual = hoy.getMonth() + 1;
    const anioActual = hoy.getFullYear();

    if (anioSeleccionado > anioActual || (anioSeleccionado === anioActual && mesSeleccionado >= mesActual)) {
      setError(`No se puede exportar el mes ${mesSeleccionado}/${anioSeleccionado}. El mes aún no ha finalizado.`);
      return;
    }

    try {
      setLoading(true);
      setError('');

      let clienteId;
      if (user?.rol === 'admin') {
        clienteId = facturacion.clientes[0].cliente_id;
      } else {
        clienteId = user?.cliente_id;
      }

      const blob = await informeService.exportarPDF(clienteId, mesSeleccionado, anioSeleccionado);
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const mesNombre = meses.find(m => m.value === mesSeleccionado)?.label || mesSeleccionado;
      const clienteNombre = facturacion.clientes.find(c => c.cliente_id === clienteId)?.cliente || 'Cliente';
      a.download = `Facturacion_${clienteNombre.replace(/\s+/g, '_')}_${mesNombre}_${anioSeleccionado}.pdf`;
      
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (err) {
      setError(typeof err === 'string' ? err : err.message || 'Error al exportar PDF');
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = async () => {
    if (!facturacion || facturacion.clientes.length === 0) {
      setError('No hay datos de facturación para exportar');
      return;
    }

    const hoy = new Date();
    const mesActual = hoy.getMonth() + 1;
    const anioActual = hoy.getFullYear();

    if (anioSeleccionado > anioActual || (anioSeleccionado === anioActual && mesSeleccionado >= mesActual)) {
      setError(`No se puede exportar el mes ${mesSeleccionado}/${anioSeleccionado}. El mes aún no ha finalizado.`);
      return;
    }

    try {
      setLoading(true);
      setError('');

      let clienteId;
      if (user?.rol === 'admin') {
        clienteId = facturacion.clientes[0].cliente_id;
      } else {
        clienteId = user?.cliente_id;
      }

      const blob = await informeService.exportarExcel(clienteId, mesSeleccionado, anioSeleccionado);
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const mesNombre = meses.find(m => m.value === mesSeleccionado)?.label || mesSeleccionado;
      const clienteNombre = facturacion.clientes.find(c => c.cliente_id === clienteId)?.cliente || 'Cliente';
      a.download = `Facturacion_${clienteNombre.replace(/\s+/g, '_')}_${mesNombre}_${anioSeleccionado}.xlsx`;
      
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (err) {
      setError(typeof err === 'string' ? err : err.message || 'Error al exportar Excel');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-NI', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      activo: 'bg-green-100 text-green-800',
      inactivo: 'bg-gray-100 text-gray-800',
      suspendido: 'bg-yellow-100 text-yellow-800'
    };
    return badges[estado] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className="w-64 bg-gradient-to-b from-slate-900 to-blue-900 text-white">
        <div className="p-6 border-b border-blue-800">
          <div className="flex items-center gap-3">
            <div className="bg-blue-500 p-2 rounded-lg">
              <Ship size={24} />
            </div>
            <div>
              <h1 className="font-bold text-lg">MarineMonitor</h1>
              <p className="text-xs text-blue-200">Multi-Plataforma</p>
            </div>
          </div>
        </div>

        <nav className="p-4 space-y-2">
          <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition">
            <BarChart3 size={20} />
            <span>Dashboard</span>
          </Link>
          <Link to="/equipos" className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition">
            <Package size={20} />
            <span>Equipos</span>
          </Link>
          <Link to="/tipos-equipo" className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition">
            <Layers size={20} />
            <span>Tipos de Equipo</span>
          </Link>
          {user?.rol === 'admin' && (
            <Link to="/clientes" className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition">
              <Building2 size={20} />
              <span>Clientes</span>
            </Link>
          )}
          {user?.rol === 'admin' && (
            <Link to="/users" className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition">
              <Users size={20} />
              <span>Usuarios</span>
            </Link>
          )}
          <Link to="/solicitudes" className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition">
            <FileText size={20} />
            <span>Solicitudes</span>
          </Link>
          <Link to="/informes" className="flex items-center gap-3 px-4 py-3 bg-blue-800 text-white rounded-lg">
            <FileText size={20} />
            <span>Informes</span>
          </Link>
        </nav>

        <div className="absolute bottom-0 w-64 p-4 border-t border-blue-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">{user?.nombre}</p>
              <p className="text-xs text-blue-200">{user?.rol}</p>
            </div>
            <button onClick={handleLogout} className="p-2 hover:bg-blue-800 rounded-lg transition" title="Cerrar Sesión">
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="bg-white shadow-sm p-6">
          <h1 className="text-3xl font-bold text-gray-800">Informes y Reportes</h1>
          <p className="text-gray-600 mt-1">Análisis detallado de equipos y facturación</p>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-red-700 text-sm">{typeof error === 'string' ? error : error.message || 'Error desconocido'}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-xl text-gray-500">Cargando reportes...</div>
            </div>
          ) : (
            <>
              {/* Gráficos */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Equipos por Tipo */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-gray-800">Equipos por Tipo</h2>
                    <button
                      onClick={() => exportarCSV(equiposPorTipo, 'equipos-por-tipo')}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Download size={20} />
                    </button>
                  </div>
                  {equiposPorTipo.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={equiposPorTipo}
                          dataKey="cantidad"
                          nameKey="tipo"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label
                        >
                          {equiposPorTipo.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500 text-center py-12">No hay datos disponibles</p>
                  )}
                </div>

                {/* Equipos por Cliente */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-gray-800">Equipos por Cliente</h2>
                    <button
                      onClick={() => exportarCSV(equiposPorCliente, 'equipos-por-cliente')}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Download size={20} />
                    </button>
                  </div>
                  {equiposPorCliente.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={equiposPorCliente}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="cliente" angle={-45} textAnchor="end" height={100} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="cantidad" fill="#3b82f6" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500 text-center py-12">No hay datos disponibles</p>
                  )}
                </div>
              </div>

              {/* Facturación Mensual */}
              {(user?.rol === 'admin' || user?.rol === 'cliente') && facturacion && (
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <DollarSign className="text-green-600" size={24} />
                      <h2 className="text-lg font-bold text-gray-800">Facturación Mensual</h2>
                    </div>
                    <div className="flex items-center gap-3">
                      <select
                        value={mesSeleccionado}
                        onChange={(e) => setMesSeleccionado(parseInt(e.target.value))}
                        className="px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        {meses.map(m => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </select>
                      <select
                        value={anioSeleccionado}
                        onChange={(e) => setAnioSeleccionado(parseInt(e.target.value))}
                        className="px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        {[2024, 2025, 2026].map(y => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                      
                      <button
                        onClick={exportarPDF}
                        disabled={loading || !facturacion}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                        title="Exportar a PDF"
                      >
                        <Download size={18} />
                        PDF
                      </button>
                      
                      <button
                        onClick={exportarExcel}
                        disabled={loading || !facturacion}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                        title="Exportar a Excel"
                      >
                        <Download size={18} />
                        Excel
                      </button>
                    </div>
                  </div>

                  {facturacion?.periodo?.en_curso && facturacion?.nota_mes_en_curso && (
                    <div className="mb-4 flex items-start gap-2 rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                      <AlertCircle className="mt-0.5 flex-shrink-0" size={18} />
                      <span>{facturacion.nota_mes_en_curso}</span>
                    </div>
                  )}

                  <div className="mb-4 p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-gray-600">Total General</p>
                    <p className="text-3xl font-bold text-green-600">
                      ${facturacion.total_general.toLocaleString()}
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">RFC</th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Equipos</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Subtotal</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {facturacion.clientes.map((cliente, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">{cliente.cliente}</td>
                            <td className="px-6 py-4 text-sm text-gray-500">{cliente.rfc}</td>
                            <td className="px-6 py-4 text-sm text-center text-gray-500">{cliente.cantidad_equipos}</td>
                            <td className="px-6 py-4 text-sm text-right font-medium text-gray-900">
                              ${cliente.subtotal.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button
                                onClick={() => loadDetalleFacturacion(cliente.cliente_id)}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                Ver Detalle
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Historial de Cambios */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="text-blue-600" size={24} />
                    <h2 className="text-lg font-bold text-gray-800">Historial de Cambios de Estado</h2>
                    {loadingHistorial && (
                      <span className="text-sm text-gray-400 animate-pulse">Actualizando…</span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      value={historialPreset}
                      onChange={(e) => setHistorialPreset(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="30dias">Últimos 30 días</option>
                      <option value="este_mes">Este mes</option>
                      <option value="mes_anterior">Mes anterior</option>
                      <option value="personalizado">Personalizado</option>
                    </select>
                    {historialPreset === 'personalizado' && (
                      <>
                        <input
                          type="date"
                          value={historialInicio}
                          max={historialFin || undefined}
                          onChange={(e) => setHistorialInicio(e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg"
                          title="Desde"
                        />
                        <span className="text-gray-400">—</span>
                        <input
                          type="date"
                          value={historialFin}
                          min={historialInicio || undefined}
                          onChange={(e) => setHistorialFin(e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg"
                          title="Hasta"
                        />
                      </>
                    )}
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Equipo</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Embarcación</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado Anterior</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado Nuevo</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      </tr>
                    </thead>
                    <tbody className={`bg-white divide-y divide-gray-200 transition-opacity ${loadingHistorial ? 'opacity-50' : ''}`}>
                      {historial.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                            {loadingHistorial ? 'Cargando historial…' : 'No hay cambios registrados en este período'}
                          </td>
                        </tr>
                      ) : (
                        historial.map((h) => (
                          <tr key={h.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">{h.equipo_numero_serie}</td>
                            <td className="px-6 py-4 text-sm text-gray-500">{h.equipo_embarcacion}</td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getEstadoBadge(h.estado_anterior)}`}>
                                {h.estado_anterior || 'N/A'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getEstadoBadge(h.estado_nuevo)}`}>
                                {h.estado_nuevo}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">{h.usuario}</td>
                            <td className="px-6 py-4 text-sm text-gray-500">{formatDate(h.fecha)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal de Detalle de Facturación */}
      {showDetalle && detalleData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Detalle de Facturación</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {detalleData.cliente.nombre} - {meses.find(m => m.value === detalleData.periodo.mes)?.label} {detalleData.periodo.anio}
                </p>
              </div>
              <button
                onClick={handleCerrarDetalle}
                className="text-gray-500 hover:text-gray-700"
                title="Cerrar"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Nota:</strong> {detalleData.nota_facturacion}
                </p>
              </div>

              {detalleData?.periodo?.en_curso && detalleData?.nota_mes_en_curso && (
                <div className="mb-6 flex items-start gap-2 rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                  <AlertCircle className="mt-0.5 flex-shrink-0" size={18} />
                  <span>{detalleData.nota_mes_en_curso}</span>
                </div>
              )}

              <div className="mb-6 grid grid-cols-3 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">RFC</p>
                  <p className="text-lg font-semibold text-gray-900">{detalleData.cliente.rfc}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Total Equipos</p>
                  <p className="text-lg font-semibold text-gray-900">{detalleData.equipos.length}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Total a Facturar</p>
                  <p className="text-2xl font-bold text-green-600">
                    ${detalleData.total_general.toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IMEI</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Embarcación</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Tarifa</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Días Activos</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Inactivación</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">¿Se Factura?</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {detalleData.equipos.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                          No hay equipos con actividad en este período
                        </td>
                      </tr>
                    ) : (
                      [...detalleData.equipos]
                        .sort((a, b) => (a.numero_serie || '').localeCompare(b.numero_serie || ''))
                        .map((equipo) => (
                        <tr key={equipo.equipo_id} className={!equipo.se_factura ? 'bg-gray-50' : 'hover:bg-gray-50'}>
                          <td className="px-4 py-4 text-sm font-medium text-gray-900">
                            {equipo.numero_serie}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700">
                            {equipo.embarcacion}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600">
                            {equipo.tipo_equipo}
                          </td>
                          <td className="px-4 py-4 text-sm text-right text-gray-900">
                            ${equipo.tarifa_mensual.toLocaleString()}
                          </td>
                          <td className="px-4 py-4 text-sm text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              equipo.dias_activos >= 5 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {equipo.dias_activos} / {equipo.total_dias_mes}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600">
                            {equipo.fecha_inactivacion ? (
                              <div>
                                <p>{new Date(equipo.fecha_inactivacion).toLocaleDateString('es-NI')}</p>
                                <p className="text-xs text-gray-500">({equipo.estado_final})</p>
                              </div>
                            ) : (
                              <span className="text-gray-400">Activo todo el mes</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-center">
                            {equipo.se_factura ? (
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                                Sí
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-semibold">
                                No
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm text-right font-semibold text-gray-900">
                            ${equipo.subtotal.toLocaleString()}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="7" className="px-4 py-4 text-right text-sm font-bold text-gray-900">
                        TOTAL:
                      </td>
                      <td className="px-4 py-4 text-right text-lg font-bold text-green-600">
                        ${detalleData.total_general.toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleCerrarDetalle}
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Informes;